"use client"

import { useEffect, useState } from "react"
import Link from "next/link"
import { useRouter } from "next/navigation"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import * as z from "zod"
import axios from "axios"
import { toast } from "sonner"
import Cookies from "js-cookie"

import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { AuthCardShell } from "@/components/auth/auth-card-shell"
import { Loader2, ArrowLeft } from "lucide-react"
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form"
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp"

import { useRole } from "@/contexts/role-context"
import { usePreferences } from "@/contexts/preferences-context"
import apiClient, { setAuthContext, resolveAuthContextFromRole } from "@/lib/axios-client" 
import { API_ENDPOINTS } from "@/lib/api-endpoints"
import { COOKIE_KEYS } from "@/lib/cookie-keys"


// --- SCHEMAS ---
const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

type ResponseLike = {
    status: number
    data?: {
        message?: string
    }
}

interface LoginFormProps {
    dict: {
        back_to_site: string
        title: string
        subtitle: string
        email_label: string
        email_placeholder: string
        password_label: string
        remember_me: string
        forgot_password: string
        submit_button: string
        no_account: string
        register_link: string
        verify_2fa_title: string
        verify_2fa_subtitle: string
        verify_button: string
        back_to_login: string
        [key: string]: string
    }
    lang: string
}

const createLoginSchema = (dict: LoginFormProps["dict"]) => z.object({
    email: z.string()
        .min(1, dict.validation_email_required)
        .regex(emailRegex, dict.validation_email_invalid),
    password: z.string().min(1, dict.validation_password_required),
})

const createOtpSchema = (dict: LoginFormProps["dict"]) => z.object({
    code: z.string().min(6, dict.validation_code_min_length),
})

type LoginFormValues = z.infer<ReturnType<typeof createLoginSchema>>
type OtpFormValues = z.infer<ReturnType<typeof createOtpSchema>>

const extractRoleFromPayload = (payload: unknown): string | undefined => {
    if (!payload || typeof payload !== "object") return undefined

    const root = payload as Record<string, unknown>
    const data = root.data && typeof root.data === "object" ? (root.data as Record<string, unknown>) : undefined
    const user = data?.user && typeof data.user === "object" ? (data.user as Record<string, unknown>) : undefined

    const candidate = root.role ?? data?.role ?? data?.userRole ?? user?.role
    if (typeof candidate !== "string") return undefined

    const normalized = candidate.trim().toUpperCase()
    if (["BUYER", "SUPPLIER", "ADMIN", "OPERATOR"].includes(normalized)) {
        return normalized
    }

    return undefined
}

export function LoginForm({ dict, lang }: LoginFormProps) {
    const router = useRouter()
    
    // Luăm funcția login și starea userului din context
    const { login, user, isAuthenticated } = useRole()
    const { updatePreferences } = usePreferences()
    
    // Stări pentru flux
    const [step, setStep] = useState<"credentials" | "2fa">("credentials")
    const [isLoading, setIsLoading] = useState(false)
    const [rememberMe, setRememberMe] = useState(false)
    const [useBackupCode, setUseBackupCode] = useState(false)
    
    // Stocăm temporar datele pentru 2FA
    const [tempAuthData, setTempAuthData] = useState<{ email: string; twoFactorToken?: string; role?: string } | null>(null)
    const welcomeBackLabel = dict.toast_welcome_back
    const welcomeBackDescLabel = dict.toast_welcome_back_desc
    const loginSchema = createLoginSchema(dict)
    const otpSchema = createOtpSchema(dict)

    // --- FORMULAR 1: EMAIL & PASSWORD ---
    const loginForm = useForm<LoginFormValues>({
        resolver: zodResolver(loginSchema),
        defaultValues: { email: "", password: "" },
    })

    // --- FORMULAR 2: OTP (2FA) ---
    const otpForm = useForm<OtpFormValues>({
        resolver: zodResolver(otpSchema),
        defaultValues: { code: "" },
    })

    // Incarcam datele la pornire (Remember Me)
    useEffect(() => {
        const savedEmail = Cookies.get(COOKIE_KEYS.rememberEmail)
        if (savedEmail) {
            setRememberMe(true)
            loginForm.setValue("email", savedEmail)
        }
    }, [loginForm])

    // --- CRITIC: EFECT PENTRU REDIRECT AUTOMAT ---
    // Așteptăm ca 'login()' să termine fetch-ul și Contextul să actualizeze user-ul.
    // Abia atunci știm ROLUL și putem face redirect corect.
    useEffect(() => {
        if (isAuthenticated && user) {
            const routeRole = user.role.toLowerCase()
            
            // Succes vizual
            toast.success(`${welcomeBackLabel} ${user.firstName}!`, {
                description: `${welcomeBackDescLabel} ${user.role}`,
            })

            // Redirect către dashboard
            router.push(`/${lang}/dashboard/${routeRole}`)
            router.refresh()
        }
    }, [isAuthenticated, user, router, lang, welcomeBackLabel, welcomeBackDescLabel])

    // --- HELPER: Finalizează Login-ul (Fără parametri acum) ---
    const handleLoginSuccess = async () => {
        // Gestionare Remember Me (Doar Email, fără date sensibile)
        const currentEmail = loginForm.getValues("email") || tempAuthData?.email;
        if (rememberMe && currentEmail) {
            Cookies.set(COOKIE_KEYS.rememberEmail, currentEmail, { expires: Number(process.env.NEXT_PUBLIC_REMEMBER_ME_DAYS || "30"), path: '/', secure: true, sameSite: 'Strict' })
        } else {
            Cookies.remove(COOKIE_KEYS.rememberEmail, { path: '/' })
        }

        // DECLANȘĂM LOGICA DIN CONTEXT
        // Contextul va face: GET /auth/user/profile -> Update User State -> Trigger useEffect de sus
        await login();
    }

    // --- PASUL 1: SUBMIT CREDENTIALS ---
    async function onCredentialsSubmit(data: LoginFormValues) {
        setIsLoading(true)
        try {
            // 1. POST Login (Setează cookie-urile invizibile automat)
            // Același endpoint pentru TOATE rolurile: BUYER, SUPPLIER, ADMIN, OPERATOR
            const response = await apiClient.post(
                API_ENDPOINTS.LOGIN,
                {
                    email: data.email,
                    password: data.password,
                },
                {
                    // Treat 4xx as handled responses to avoid dev overlay errors.
                    validateStatus: (status) => status >= 200 && status < 500,
                }
            );

            if (response.status >= 400) {
                handleError(response)
                return
            }

            const payload = response.data;
            
            // Caută în 4 moduri: flag explicit la rădăcină, în data, mesaj
            let requiresTwoFactor =
                payload?.requiresTwoFactor ||
                payload?.twoFactorRequired ||
                payload?.requires_2fa ||
                payload?.data?.requiresTwoFactor ||     // ← IMPORTANT!
                payload?.data?.twoFactorRequired;
            
            // Fallback: Parsează mesajul
            if (!requiresTwoFactor && payload?.message?.toLowerCase?.().includes("2fa")) {
                requiresTwoFactor = true;
            }
            
            // Caută token-ul - verifică și în data object
            const twoFactorToken = 
                payload?.twoFactorToken || 
                payload?.tempToken ||
                payload?.token ||
                payload?.data?.twoFactorToken ||        // ← IMPORTANT!
                payload?.data?.tempToken ||
                payload?.data?.token;

            // Detectăm rolul din răspunsul login-ului
            const userRole = extractRoleFromPayload(payload);

            // SCENARIUL A: 2FA
            if (requiresTwoFactor) {
                setTempAuthData({ email: data.email, twoFactorToken, role: userRole })
                setStep("2fa")
                toast.info(dict.toast_2fa_required)
            } 
            // SCENARIUL B: Login Direct
            else {
                // Setăm contextul de autentificare ÎNAINTE de a cere profilul
                const authContext = resolveAuthContextFromRole(userRole)
                if (authContext) {
                    setAuthContext(authContext)
                }

                // Așteaptă puțin ca browser-ul să proceseze cookie-urile
                await new Promise(resolve => setTimeout(resolve, 100))
                
                // Nu mai construim userul manual. Doar apelăm succesul.
                await handleLoginSuccess()
            }

        } catch (error: unknown) {
            handleError(error)
        } finally {
            // Nu oprim loading-ul dacă mergem spre redirect (ca să nu pâlpâie butonul)
            // Dar dacă e eroare sau trecem la pasul 2, îl oprim.
            // Aici e safe să-l oprim, că useEffect-ul de redirect va prelua.
            setIsLoading(false)
        }
    }

    // --- PASUL 2: SUBMIT OTP ---
    async function onOtpSubmit(data: OtpFormValues) {
        setIsLoading(true)
        try {
            // Backend folosește ACELAȘI endpoint pentru toți userii
            const verify2faEndpoint = API_ENDPOINTS.VERIFY_2FA;
            
            if (!tempAuthData?.twoFactorToken) {
                toast.error(dict.toast_session_expired, {
                    description: dict.toast_session_expired_desc
                });
                setStep("credentials");
                setIsLoading(false);
                return;
            }
            
            // Payload conform documentației backend - DOAR code și twoFactorToken
            const payload = {
                code: data.code,
                twoFactorToken: tempAuthData.twoFactorToken
            };
            
            // 2FA Verify (Backend-ul validează și setează cookie-urile finale)
            await apiClient.post(verify2faEndpoint, payload);

            // Actualizează preferences cu 2FA enabled
            updatePreferences({ twoFactorEnabled: true })

            // Setăm contextul de autentificare din rolul stocat la pasul 1
            const authContext = resolveAuthContextFromRole(tempAuthData?.role)
            if (authContext) {
                setAuthContext(authContext)
            }
            
            // Așteaptă ca browser-ul să proceseze noile cookie-uri
            await new Promise(resolve => setTimeout(resolve, 150))

            // Dacă e succes, apelăm logica de încărcare profil
            await handleLoginSuccess()

        } catch (error: unknown) {
            handleError(error)
            otpForm.reset()
            setIsLoading(false)
        }
    }

    // --- HELPER: Error Handling ---
    const handleError = (error: unknown) => {
        const responseFromStatusLike: ResponseLike | null =
            typeof error === "object" && error !== null && "status" in error
                ? (error as ResponseLike)
                : null

        const response = axios.isAxiosError(error)
            ? error.response
            : responseFromStatusLike

        if (response) {
            const status = response.status
            const message = response.data?.message
            const messageText = typeof message === "string" ? message : ""
            const normalizedMessage = messageText.toLowerCase()
            
            if (status === 409) {
                toast.error(dict.toast_already_logged_in, {
                    description: message || dict.toast_already_logged_in_desc,
                    duration: 6000,
                    action: {
                        label: dict.toast_go_to_dashboard,
                        onClick: () => {
                            if (user) {
                                const routeRole = user.role.toLowerCase()
                                router.push(`/${lang}/dashboard/${routeRole}`)
                            }
                        }
                    }
                })
            } else if (status === 401) {
                const isUserMissing =
                    normalizedMessage.includes("user") &&
                    (normalizedMessage.includes("not found") ||
                        normalizedMessage.includes("does not exist") ||
                        normalizedMessage.includes("not exist") ||
                        normalizedMessage.includes("no account"))

                if (isUserMissing) {
                    toast.error(dict.toast_user_not_found, {
                        description: messageText || dict.toast_user_not_found_desc,
                    })
                } else {
                    toast.error(dict.toast_auth_failed, {
                        description: messageText || dict.toast_auth_failed_invalid,
                    })
                }
            } else if (status === 429) {
                toast.error(dict.toast_too_many_attempts, {
                    description: message || dict.toast_too_many_attempts_desc,
                    duration: 5000,
                })
            } else {
                toast.error(dict.toast_auth_failed, {
                    description: message || dict.toast_auth_failed_generic,
                })
            }
        } else {
            toast.error(dict.toast_auth_failed, {
                description: dict.toast_auth_failed_network,
            })
        }
    }

    return (
        <AuthCardShell
            title={step === "credentials" ? dict.title : dict.verify_2fa_title || "Two-Factor Authentication"}
            description={
                step === "credentials" 
                    ? undefined
                    : useBackupCode 
                        ? (dict.verify_backup_code_subtitle || "Enter one of your backup codes.")
                        : (dict.verify_2fa_subtitle || "Enter the 6-digit code from your authenticator app.")
            }
            backHref={step === "credentials" ? `/${lang}/` : undefined}
            backLabel={step === "credentials" ? dict.back_to_site : undefined}
        >
            <div className="space-y-4">
                
                {/* --- PASUL 1: EMAIL & PASSWORD --- */}
                {step === "credentials" && (
                    <>
                        <Form {...loginForm}>
                            <form onSubmit={loginForm.handleSubmit(onCredentialsSubmit)} className="space-y-4">
                                <FormField
                                    control={loginForm.control}
                                    name="email"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel>{dict.email_label}</FormLabel>
                                            <FormControl>
                                                <Input
                                                    placeholder={dict.email_placeholder}
                                                    type="email"
                                                    autoComplete="email"
                                                    {...field}
                                                />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />

                                <FormField
                                    control={loginForm.control}
                                    name="password"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel>{dict.password_label}</FormLabel>
                                            <FormControl>
                                                <Input
                                                    placeholder="••••••••"
                                                    type="password"
                                                    autoComplete="current-password"
                                                    {...field}
                                                />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />

                                <div className="flex items-center justify-between text-sm">
                                    <label className="flex items-center gap-2 cursor-pointer">
                                        <input type="checkbox"
                                            className="rounded border-gray-300"
                                            checked={rememberMe}
                                            onChange={(e) => setRememberMe(e.target.checked)}
                                        />
                                        <span className="text-muted-foreground">{dict.remember_me}</span>
                                    </label>
                                    <Link href={`/${lang}/forgot-password`} className="text-primary hover:underline">
                                        {dict.forgot_password}
                                    </Link>
                                </div>

                                <Button className="w-full" size="lg" type="submit" disabled={isLoading}>
                                    {isLoading ? (
                                        <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> {dict.signing_in}</>
                                    ) : (
                                        dict.submit_button
                                    )}
                                </Button>
                            </form>
                        </Form>

                        <div className="text-center text-sm text-muted-foreground pt-2">
                            {dict.no_account}{" "}
                            <Link href={`/${lang}/register`} className="text-primary hover:underline">
                                {dict.register_link}
                            </Link>
                        </div>
                    </>
                )}

                {/* --- PASUL 2: OTP INPUT --- */}
                {step === "2fa" && (
                    <Form {...otpForm}>
                        <form onSubmit={otpForm.handleSubmit(onOtpSubmit)} className="space-y-6">
                            <FormField
                                control={otpForm.control}
                                name="code"
                                render={({ field }) => (
                                    <FormItem className="flex flex-col items-center justify-center">
                                        <FormControl>
                                            {useBackupCode ? (
                                                <Input
                                                    placeholder="ABCD-1234-EFGH"
                                                    className="text-center font-mono text-lg tracking-wider uppercase"
                                                    maxLength={20}
                                                    autoComplete="off"
                                                    {...field}
                                                    onChange={(e) => {
                                                        field.onChange(e.target.value.toUpperCase())
                                                    }}
                                                />
                                            ) : (
                                                <InputOTP maxLength={6} {...field}>
                                                    <InputOTPGroup>
                                                        <InputOTPSlot index={0} />
                                                        <InputOTPSlot index={1} />
                                                        <InputOTPSlot index={2} />
                                                        <InputOTPSlot index={3} />
                                                        <InputOTPSlot index={4} />
                                                        <InputOTPSlot index={5} />
                                                    </InputOTPGroup>
                                                </InputOTP>
                                            )}
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            {/* Toggle între Authenticator și Backup Code */}
                            <div className="text-center">
                                <button
                                    type="button"
                                    onClick={() => {
                                        setUseBackupCode(!useBackupCode)
                                        otpForm.reset()
                                    }}
                                    className="text-sm text-primary hover:underline"
                                    disabled={isLoading}
                                >
                                    {useBackupCode 
                                        ? dict.use_authenticator
                                        : dict.use_backup_code
                                    }
                                </button>
                            </div>

                            <div className="space-y-2">
                                <Button className="w-full" size="lg" type="submit" disabled={isLoading}>
                                    {isLoading ? (
                                        <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> {dict.verifying}</>
                                    ) : (
                                        dict.verify_button
                                    )}
                                </Button>
                                <Button 
                                    type="button" 
                                    variant="ghost" 
                                    className="w-full" 
                                    onClick={() => setStep("credentials")}
                                    disabled={isLoading}
                                >
                                    <ArrowLeft className="mr-2 h-4 w-4" />
                                    {dict.back_to_login || "Back to Login"}
                                </Button>
                            </div>
                        </form>
                    </Form>
                )}
            </div>
        </AuthCardShell>
    )
}